import {
  ChatHttpError,
  type ChatClient,
  type ChatDeliveryEvent,
  type ChatMessage,
  type ChatReaction,
  type ConnectionState,
} from '../client';
import { createWidgetState, type WidgetConnectionStatus } from './state';
import {
  applyWidgetTheme,
  createWidgetDom,
  injectWidgetStyles,
  type WidgetDomElements,
  type WidgetTheme,
} from './dom';
import { bindWidgetEvents, type WidgetEventBindings } from './events';
import { createMessageListRenderer, type MessageListRenderer } from './messages';
import { createMessageMenu, type MessageMenu } from './messageMenu';
import { createComposer, type Composer, type PendingFile } from './composer';
import {
  createLoginRequiredScreen,
  type LoginRequiredOptions,
  type LoginRequiredScreen,
} from './loginRequired';
import { createNameRequiredPanel, type NameRequiredPanel } from './nameRequired';
import { bellIcon, bellOffIcon, chatBubbleIcon, closeIcon, soundOffIcon, soundOnIcon } from './icons';
import { playNotificationSound, prepareNotificationSound } from './notificationSound';
import {
  getNotificationPermission,
  isNotificationSupported,
  requestNotificationPermission,
  showBrowserNotification,
} from './browserNotifications';
import {
  createCustomerInfoPanel,
  type CustomerInfoPanel,
  type CustomerInfoProvider,
} from './customerInfo';
import { createHistoryViewer, createRecentConversationsSection } from './conversationHistory';
import { createGreetingTeaser, type GreetingTeaser, type GreetingTeaserContent } from './greetingTeaser';
import { t, tf } from './i18n';

// A short, calm delay before the greeting teaser appears — long enough to
// read as a considered "can I help?" rather than an instant pop-up, but
// quick enough to catch the customer while they're still deciding.
const TEASER_SHOW_DELAY_MS = 4000;

// The bootstrap route's distinct "no display name yet" signal — see
// features/customers' CustomerNameRequiredError, mapped to this exact
// status in features/http/errors.ts specifically so it's detectable here by
// status code alone, no message-string matching.
const NAME_REQUIRED_STATUS = 428;

// Client-side mirror of the server's MAX_UPLOAD_BYTES (50 MiB) — must be
// kept in sync with the production env (and nginx's client_max_body_size)
// whenever the limit changes. Purely a UX shortcut: an oversized file fails
// instantly with a clear message instead of uploading for minutes only to
// be rejected; the server remains the actual enforcer.
const MAX_UPLOAD_BYTES = 52_428_800;

export interface CreateWidgetOptions {
  client: ChatClient;
  // Absent for a logged-out visitor. Only logged-in customers may create
  // conversations (see docs/SecurityReview-2026-07-17.md, H1) — this is
  // unchanged. Without it, initialize() renders the login-required screen
  // below instead of the chat, rather than the widget not rendering at all.
  customerId?: string;
  loginUrl?: string;
  registerUrl?: string;
  // How the login-required screen should behave when the host knows more
  // than "nobody is signed in" — see LoginRequiredOptions. Undefined keeps
  // the screen exactly as it has always been.
  loginScreen?: Pick<LoginRequiredOptions, 'resolveSignInUrl' | 'onSignInStart' | 'linkMissing'>;
  theme?: WidgetTheme;
  // Extension point, not a live feature: undefined by default, so the
  // header stays non-interactive and no customer data is ever fetched or
  // shown. A caller that supplies this makes the header clickable and opens
  // the customer-info panel (see customerInfo.ts) — intentionally not
  // wired to Shopify or any other data source in this codebase.
  getCustomerInfo?: CustomerInfoProvider;
}

export interface Widget {
  initialize(): void;
  destroy(): void;
  open(): void;
  close(): void;
  toggle(): void;
}

// Truthful, generic copy — no named agent, no fabricated "online" or
// "replies in X minutes" claim, since this system has no data backing
// either (see the design integration notes: Discord gives us no staff
// presence/typing signal at all). 'creationFailed' has no header subtitle
// at all: the message-list error ("Unable to start the conversation" +
// Try again) is the one place that failure is communicated, so the header
// doesn't repeat (or worse, contradict) it with a stray "disconnected".
//
// A function rather than a module-level table: t() must not run at import
// time, before embed.ts has resolved the page locale.
function statusSubtitle(status: WidgetConnectionStatus): string {
  switch (status) {
    case 'connecting':
      return t('statusConnecting');
    case 'connected':
      return t('statusConnected');
    case 'reconnecting':
      return t('statusReconnecting');
    case 'idle':
    case 'creationFailed':
      return '';
  }
}

// Only ever invoked from handleConnectionStateChange below, which is only
// ever able to fire once options.client.connect() has been called — and
// that only happens after createConversation() already succeeded (see
// connectToConversation). So every real call here happens with a
// conversation already in hand: WS states map to 'connected' or
// 'reconnecting' only. The pre-bootstrap 'connecting' and 'creationFailed'
// states are connectToConversation's own, set directly, never through here.
function toWidgetConnectionStatus(connectionState: ConnectionState): WidgetConnectionStatus {
  switch (connectionState) {
    case 'open':
      return 'connected';
    case 'connecting':
    case 'closed':
    case 'idle':
      return 'reconnecting';
  }
}

// The Client SDK's live-update payload is untyped (unknown) — this is the
// one place that checks it looks like a ChatMessage before the rendering
// layer ever sees it. The rendering layer itself only ever receives
// ChatMessage objects.
function isChatMessage(value: unknown): value is ChatMessage {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.id === 'string' &&
    typeof candidate.senderType === 'string' &&
    typeof candidate.body === 'string' &&
    typeof candidate.createdAt === 'string' &&
    Array.isArray(candidate.attachments)
  );
}

// Pushed by the server the moment a conversation closes, from either close
// path (a customer's own "End chat", or staff running $close in Discord) —
// see app.ts's conversationService onClosed wiring. Distinct shape from
// ChatMessage (no id/senderType/body/attachments), so it can never be
// mistaken for one by isChatMessage above.
interface ConversationClosedEvent {
  event: 'conversation_closed';
  conversationId: string;
}

function isConversationClosedEvent(value: unknown): value is ConversationClosedEvent {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return candidate.event === 'conversation_closed' && typeof candidate.conversationId === 'string';
}

// A reaction added/removed on a message from either side (a customer's own
// other tab, or a support member on Discord) — carries the message's full
// updated reaction summary.
interface MessageReactionEvent {
  event: 'message_reaction';
  conversationId: string;
  messageId: string;
  reactions: ChatReaction[];
}

function isMessageReactionEvent(value: unknown): value is MessageReactionEvent {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    candidate.event === 'message_reaction' &&
    typeof candidate.messageId === 'string' &&
    Array.isArray(candidate.reactions)
  );
}

// One of the customer's own messages reached support, or was given up on.
function isMessageDeliveryEvent(value: unknown): value is ChatDeliveryEvent {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    candidate.event === 'message_delivery' &&
    typeof candidate.messageId === 'string' &&
    (candidate.delivery === 'sending' ||
      candidate.delivery === 'delivered' ||
      candidate.delivery === 'failed' ||
      candidate.delivery === 'review')
  );
}

// A support member is typing in this conversation's Discord channel right
// now. There's no matching "stopped" push (Discord doesn't send one to us
// either) — the widget clears this itself after a short timeout or the
// moment a real message arrives, whichever comes first.
interface StaffTypingEvent {
  event: 'staff_typing';
  conversationId: string;
  staffName: string;
}

function isStaffTypingEvent(value: unknown): value is StaffTypingEvent {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return candidate.event === 'staff_typing' && typeof candidate.staffName === 'string';
}

// Pushed the moment a staff member claims the ticket in Discord (see
// app.ts's conversationService onClaimed wiring) — lets an already-open
// widget reflect it live, not just on the next reload/bootstrap.
interface TicketClaimedEvent {
  event: 'ticket_claimed';
  conversationId: string;
  staffName: string;
  staffAvatarUrl: string | null;
}

function isTicketClaimedEvent(value: unknown): value is TicketClaimedEvent {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  // staffAvatarUrl is treated as optional here even though the server
  // always sends it today — a payload missing it entirely (an older
  // deployment, a proxy that strips unknown fields) should still be
  // accepted as a valid claim event rather than silently dropped; the
  // caller normalizes undefined to null (see handleIncomingPayload).
  return (
    candidate.event === 'ticket_claimed' &&
    typeof candidate.conversationId === 'string' &&
    typeof candidate.staffName === 'string' &&
    (candidate.staffAvatarUrl === undefined ||
      candidate.staffAvatarUrl === null ||
      typeof candidate.staffAvatarUrl === 'string')
  );
}

// Mirrors the open/close CSS animation duration (--fcw-motion-panel) — a
// safety net only, in case animationend never fires (e.g. the panel gets
// detached mid-animation), so a pending close can never leave the widget
// stuck half-hidden.
const CLOSE_ANIMATION_FALLBACK_MS = 300;

// Which content elements.body currently shows: the live conversation
// (including the home screen, which is the live view with no active
// conversation) or the read-only view of one past conversation, entered
// from the home screen's recent-conversations rows. Independent from the
// pre-existing customer-info panel, which still just swaps elements.body
// directly and returns via showLiveConversation().
type BodyView = 'live' | 'historyViewer';

export function createWidget(options: CreateWidgetOptions): Widget {
  const state = createWidgetState();

  let elements: WidgetDomElements | null = null;
  let eventBindings: WidgetEventBindings | null = null;
  let messageList: MessageListRenderer | null = null;
  let composer: Composer | null = null;
  let messageMenu: MessageMenu | null = null;
  let customerInfoPanel: CustomerInfoPanel | null = null;
  let nameRequiredPanel: NameRequiredPanel | null = null;
  let bodyView: BodyView = 'live';
  // The message currently being replied to (set from the actions menu),
  // included on the next send and then cleared.
  let replyingToMessage: ChatMessage | null = null;
  // What the home screen's headline should say: the neutral first-visit
  // prompt until a conversation actually ends, after which returning to
  // home (from anywhere) keeps showing the "ended" phrasing.
  let homeMessage: string | null = null;
  // Mirrors every composer?.setReady(...) call — tracked here (rather than
  // reading it back off the Composer instance, which has no such getter)
  // purely so open() knows where focus should land: the composer once it
  // can actually accept typing, the dialog itself otherwise (login-required
  // screen, or before a conversation has connected).
  let composerReady = false;
  // Non-null exactly while the close-animation is in flight — lets a second
  // close() (e.g. Escape pressed twice) no-op instead of stacking a second
  // animationend listener, and lets open() cancel a still-running close
  // cleanly if it's invoked before the animation finishes.
  let pendingClose: (() => void) | null = null;
  // Only for a logged-out visitor — see initialize().
  let loginRequired: LoginRequiredScreen | null = null;
  let unsubscribeConnectionState: (() => void) | null = null;
  let unsubscribeMessages: (() => void) | null = null;
  // The host page's own title, captured at init and re-captured whenever
  // the page changes it — the "(N) " unread prefix is always applied on
  // top of this, never baked into it (see updatePageTitle).
  let baseTitle = document.title;
  let detachVisibilityListener: (() => void) | null = null;

  // ---- Greeting teaser ----
  let greetingTeaser: GreetingTeaser | null = null;
  let teaserShowTimer: ReturnType<typeof setTimeout> | null = null;
  let detachTeaserKeydown: (() => void) | null = null;
  let detachDragAndDrop: (() => void) | null = null;
  // The customer's resolved display name (from getCustomerProfile) — kept so
  // the teaser can say "Welcome back, {name}" without another fetch. Null
  // until/unless one resolves.
  let customerDisplayName: string | null = null;
  // Set true the moment a conversation ends (customer End-chat, or a
  // close discovered on resume) and cleared when a new one connects — the
  // signal for the teaser's "Conversation closed" case (CASE 5).
  let conversationJustEnded = false;
  // Auto-clears the "{name} is typing…" indicator — Discord's own typing
  // signal expires after ~10s of silence and sends no explicit "stopped"
  // event, so the widget times it out itself rather than relying on one.
  const STAFF_TYPING_TIMEOUT_MS = 6000;
  let staffTypingTimer: ReturnType<typeof setTimeout> | null = null;
  // Whether a wired order provider reported an in-progress order (CASE 3).
  // Stays false unless CreateWidgetOptions.getCustomerInfo is supplied AND
  // yields a pending order — the widget never fabricates order state.
  let hasPendingOrder = false;

  function renderOpenState(): void {
    if (!elements) {
      return;
    }

    const isOpen = state.getState().openState === 'open';
    elements.panel.hidden = !isOpen;
    if (isOpen) {
      // The first time a logged-out visitor actually sees the sign-in
      // prompt is when it finds out where sign-in should go.
      loginRequired?.activate();
    }
    elements.floatingButton.setAttribute('aria-expanded', String(isOpen));
    elements.floatingButton.setAttribute(
      'aria-label',
      isOpen ? t('launcherClose') : t('launcherOpen'),
    );

    elements.floatingButtonIcon.replaceChildren(isOpen ? closeIcon() : chatBubbleIcon());
    // Matches the design's distinct "close" launcher treatment (neutral dark
    // surface, not the accent gradient) — see the --launcher--open rules.
    elements.floatingButton.classList.toggle('flix-chat-widget__launcher--open', isOpen);
  }

  function renderConnectionStatus(): void {
    if (!elements) {
      return;
    }

    const { connectionStatus: status, assignedStaffName } = state.getState();
    // A real, named staff member (set by an actual Discord claim) is more
    // useful to a customer than the generic reply-time estimate — replaces
    // it rather than adding a second line, so the header stays one row.
    elements.statusLabel.textContent =
      status === 'connected' && assignedStaffName
        ? tf('beingHelpedBy', { name: assignedStaffName })
        : statusSubtitle(status);
    elements.headerStatusDot.classList.toggle(
      'flix-chat-widget__header-status-dot--online',
      status === 'connected',
    );
    // Only the "conversation exists, WS dropped, auto-retrying" state shows
    // this — never 'creationFailed', where there's no conversation and
    // nothing is actually retrying in the background (see statusSubtitle).
    elements.reconnectBanner.hidden = status !== 'reconnecting';
  }

  // Only shown once there's an active conversation to end — hidden again
  // right after a successful end-chat, until connectToConversation (either
  // automatic on the next send, or via the "Start new chat" control) sets a
  // fresh conversationId. The overflow trigger itself follows suit: with
  // "End chat" as its only item, there's nothing to show it for otherwise.
  function renderEndChatButton(): void {
    if (!elements) {
      return;
    }

    // The overflow menu now always carries the sound-preference toggle, so
    // it renders for every logged-in customer (this function is never
    // reached on the login-required path, where the button keeps its
    // default hidden state) — only the End-chat item stays gated on an
    // actual conversation existing.
    const hasConversation = state.getState().conversationId !== null;
    elements.endChatButton.hidden = !hasConversation;
    elements.overflowButton.hidden = false;
  }

  // ---- Unread messages: badge, page title, notification sound ----

  const UNREAD_DISPLAY_CAP = 99;
  const TITLE_PREFIX_PATTERN = /^\(\d+\+?\) /;

  function formatUnreadCount(count: number): string {
    return count > UNREAD_DISPLAY_CAP ? `${UNREAD_DISPLAY_CAP}+` : String(count);
  }

  function updatePageTitle(count: number): void {
    const currentlyPrefixed = TITLE_PREFIX_PATTERN.test(document.title);

    // The host page may change its own title at any time (SPA navigation
    // between products, most commonly) — re-capture it as the base
    // whenever the current title isn't one this widget wrote.
    if (!currentlyPrefixed && document.title !== baseTitle) {
      baseTitle = document.title;
    }

    if (count > 0) {
      document.title = `(${formatUnreadCount(count)}) ${baseTitle}`;
    } else if (currentlyPrefixed) {
      document.title = baseTitle;
    }
  }

  function renderUnreadIndicators(): void {
    const count = state.getState().unreadCount;

    if (elements) {
      elements.launcherBadge.hidden = count === 0;
      elements.launcherBadge.textContent = count === 0 ? '' : formatUnreadCount(count);

      const isOpen = state.getState().openState === 'open';
      const baseLabel = isOpen ? t('launcherClose') : t('launcherOpen');
      elements.floatingButton.setAttribute(
        'aria-label',
        count > 0 ? `${baseLabel} — ${tf('unreadBadgeAria', { count: String(count) })}` : baseLabel,
      );
    }

    updatePageTitle(count);
  }

  // "Seen" means all of it at once: panel open, the live conversation (not
  // History or a read-only past view) showing, the window/tab actually
  // focused (not just technically visible — switching to a different
  // application without hiding the tab still counts as "not looking", per
  // the browser-notifications spec's own "another window" case), and the
  // list scrolled to the latest messages.
  function isViewingLatestMessages(): boolean {
    return (
      state.getState().openState === 'open' &&
      bodyView === 'live' &&
      !document.hidden &&
      document.hasFocus() &&
      (messageList?.isScrolledToBottom() ?? false)
    );
  }

  function markMessagesRead(): void {
    if (state.getState().unreadCount !== 0) {
      state.setUnreadCount(0);
    }

    // Only clears the *live* conversation's history flag — if this runs
    // after showConversationEnded() already nulled conversationId, there's
    // nothing to clear here, which is exactly right: that conversation's
    // unread flag is meant to persist into the history list precisely
    // because the customer never actually viewed it live.
    const conversationId = state.getState().conversationId;
    if (conversationId) {
      state.markConversationRead(conversationId);
    }

    renderUnreadIndicators();

    // Reading clears every pending/shown notification for this batch too —
    // covers the customer reading via some other route (a second click, a
    // refocus) while a debounced browser notification was about to fire, or
    // closing out one that's already on screen. See scheduleBrowserNotification.
    clearPendingBrowserNotification();
    activeBrowserNotification?.close();
    activeBrowserNotification = null;
  }

  function markReadIfViewing(): void {
    if (state.getState().unreadCount > 0 && isViewingLatestMessages()) {
      markMessagesRead();
    }
  }

  // ---- Browser (OS-level) notifications ----

  const NOTIFICATION_DEBOUNCE_MS = 1500;
  const NOTIFICATION_SNIPPET_MAX_LENGTH = 80;
  let notificationDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let lastStaffMessage: ChatMessage | null = null;
  let activeBrowserNotification: Notification | null = null;
  let notificationPermissionRequested = false;

  function clearPendingBrowserNotification(): void {
    if (notificationDebounceTimer !== null) {
      clearTimeout(notificationDebounceTimer);
      notificationDebounceTimer = null;
    }

    lastStaffMessage = null;
  }

  function shouldShowBrowserNotification(): boolean {
    return (
      isNotificationSupported() &&
      getNotificationPermission() === 'granted' &&
      state.getState().browserNotificationsEnabled
    );
  }

  // Per spec: request only once the customer has actually engaged (starting
  // or resuming a conversation, or opting in via the settings toggle) —
  // never on page load. Requesting is itself idempotent (the browser no-ops
  // once permission is already granted/denied), but this flag also stops a
  // second in-flight prompt if the customer somehow triggers both entry
  // points before answering the first one.
  function maybeRequestNotificationPermission(): void {
    if (notificationPermissionRequested) {
      return;
    }

    if (!isNotificationSupported() || getNotificationPermission() !== 'default') {
      return;
    }

    notificationPermissionRequested = true;
    void requestNotificationPermission().then(() => {
      renderNotificationsToggle();
    });
  }

  function buildNotificationBody(unreadCount: number, message: ChatMessage): string {
    if (unreadCount > 1) {
      return tf('notificationAggregateBody', { count: String(unreadCount) });
    }

    const name = state.getState().assignedStaffName ?? t('notificationSupportFallbackName');

    if (!message.body || message.body.trim().length === 0) {
      return tf('notificationAttachmentBody', { name });
    }

    const snippet =
      message.body.length > NOTIFICATION_SNIPPET_MAX_LENGTH
        ? `${message.body.slice(0, NOTIFICATION_SNIPPET_MAX_LENGTH).trimEnd()}…`
        : message.body;

    return tf('notificationReplyBody', { name, snippet });
  }

  // Focuses this tab, returns to the live conversation, scrolls to the
  // newest message, and clears the unread state — the full "open the
  // notification" action from the spec's CLICK ACTION section. Opening a
  // brand-new tab when none is open is deliberately not attempted: this
  // notification only exists because THIS tab's own JavaScript created it
  // (there is no Service Worker / Push subscription behind it), so a tab
  // showing the widget is, by construction, already open.
  function openFromNotification(): void {
    if (bodyView !== 'live') {
      showLiveConversation();
    }

    open();
    markMessagesRead();
  }

  function fireBrowserNotification(): void {
    notificationDebounceTimer = null;

    const message = lastStaffMessage;
    const unreadCount = state.getState().unreadCount;

    // Re-checked at fire time, not just when scheduling: the customer may
    // have read the messages (or a resync may have cleared them) at any
    // point during the debounce window.
    if (!message || unreadCount === 0 || isViewingLatestMessages()) {
      lastStaffMessage = null;
      return;
    }

    activeBrowserNotification = showBrowserNotification({
      title: t('notificationTitle'),
      body: buildNotificationBody(unreadCount, message),
      onClick: openFromNotification,
    });

    lastStaffMessage = null;
  }

  // Batches rapid arrivals into a single notification instead of one per
  // message — see fireBrowserNotification, which reads the accumulated
  // unreadCount (not a separate counter) once the batch settles.
  function scheduleBrowserNotification(message: ChatMessage): void {
    lastStaffMessage = message;

    if (notificationDebounceTimer !== null) {
      clearTimeout(notificationDebounceTimer);
    }

    notificationDebounceTimer = setTimeout(fireBrowserNotification, NOTIFICATION_DEBOUNCE_MS);
  }

  // The single sound-or-notification decision point: exactly one of the two
  // ever fires for a given arrival, never both — a granted, enabled browser
  // notification is expected to carry its own OS-level sound, so the
  // widget's own chime is skipped specifically to avoid the customer
  // hearing two sounds for one message.
  function notifyOfUnseenStaffMessage(message: ChatMessage): void {
    if (shouldShowBrowserNotification()) {
      scheduleBrowserNotification(message);
      return;
    }

    if (state.getState().soundEnabled) {
      playNotificationSound();
    }
  }

  function registerUnreadStaffMessage(message: ChatMessage): void {
    state.setUnreadCount(state.getState().unreadCount + 1);
    // Tied to whichever conversation this connection is currently on —
    // outlives the badge if the conversation ends before it's read, so the
    // history list can still flag it (see loadRecentConversations).
    const conversationId = state.getState().conversationId;
    if (conversationId) {
      state.markConversationUnread(conversationId);
    }
    renderUnreadIndicators();
    notifyOfUnseenStaffMessage(message);
    // A reply arriving while the panel is closed also surfaces the teaser
    // (its CASE 4 "new reply" message), the same nudge Intercom/Crisp give
    // — gated by all the usual rules (closed, not dismissed, not already
    // shown) inside showTeaserIfEligible.
    showTeaserIfEligible();
  }

  function clearStaffTyping(): void {
    if (staffTypingTimer !== null) {
      clearTimeout(staffTypingTimer);
      staffTypingTimer = null;
    }
    messageList?.hideTypingIndicator();
  }

  // Renews the indicator and its timeout on every staff_typing push — a
  // staff member actively typing keeps re-triggering this (Discord itself
  // re-fires typingStart roughly every few seconds while a user keeps
  // typing), which is what keeps it visible continuously rather than
  // flickering off between events.
  function showStaffTyping(staffName: string): void {
    messageList?.showTypingIndicator(staffName);

    if (staffTypingTimer !== null) {
      clearTimeout(staffTypingTimer);
    }
    staffTypingTimer = setTimeout(() => {
      staffTypingTimer = null;
      messageList?.hideTypingIndicator();
    }, STAFF_TYPING_TIMEOUT_MS);
  }

  function renderSoundToggle(): void {
    if (!elements) {
      return;
    }

    const enabled = state.getState().soundEnabled;
    elements.soundToggleButton.replaceChildren(
      enabled ? soundOnIcon() : soundOffIcon(),
      document.createTextNode(t('soundToggleLabel')),
    );
    elements.soundToggleButton.setAttribute('aria-checked', String(enabled));
    elements.soundToggleButton.setAttribute('aria-label', t('soundToggleLabel'));
  }

  // Hidden entirely when the Notifications API doesn't exist at all — no
  // control is offered for a capability that can never work here (the
  // FALLBACK requirement: badge/sound/title keep working regardless,
  // with no error surfaced anywhere for the unsupported case). Disabled,
  // not hidden, once the browser itself has blocked notifications for this
  // site — that state can only be undone from the browser's own site
  // settings, never from inside the page.
  function renderNotificationsToggle(): void {
    if (!elements) {
      return;
    }

    if (!isNotificationSupported()) {
      elements.notificationsToggleButton.hidden = true;
      return;
    }

    const blocked = getNotificationPermission() === 'denied';
    const enabled = state.getState().browserNotificationsEnabled && !blocked;
    const label = blocked ? t('notificationsBlockedLabel') : t('notificationsToggleLabel');

    elements.notificationsToggleButton.hidden = false;
    elements.notificationsToggleButton.disabled = blocked;
    elements.notificationsToggleButton.replaceChildren(
      enabled ? bellIcon() : bellOffIcon(),
      document.createTextNode(label),
    );
    elements.notificationsToggleButton.setAttribute('aria-checked', String(enabled));
    elements.notificationsToggleButton.setAttribute('aria-label', label);
  }

  // ---- Greeting teaser lifecycle ----

  // The support person to attribute the teaser to: the real staff member
  // who claimed the conversation (their Discord avatar if we have the URL,
  // else their initial in a coloured circle) — otherwise generic support
  // (the same person glyph the header uses), never a fabricated name.
  function buildTeaserPresenter(): Pick<
    GreetingTeaserContent,
    'presenterName' | 'avatarUrl' | 'avatarInitial'
  > {
    const { assignedStaffName, assignedStaffAvatarUrl } = state.getState();

    if (!assignedStaffName) {
      return { presenterName: null, avatarUrl: null, avatarInitial: null };
    }

    return {
      presenterName: assignedStaffName,
      avatarUrl: assignedStaffAvatarUrl,
      avatarInitial: assignedStaffAvatarUrl ? null : assignedStaffName.charAt(0),
    };
  }

  // Picks the right message for the customer's current state, or null when
  // nothing should show. Priority is deliberate: an unread reply outranks
  // everything (and is the only case allowed to interrupt an active
  // conversation); otherwise a just-closed conversation, then a pending
  // order, then a returning named customer, then the first-visit welcome.
  function selectTeaserContent(): GreetingTeaserContent | null {
    const s = state.getState();
    const presenter = buildTeaserPresenter();

    // CASE 4 — new unread reply. Highest priority; shows even mid-conversation.
    if (s.unreadCount > 0) {
      return { message: t('teaserUnread'), ...presenter };
    }

    // No ordinary greeting while the customer already has a live
    // conversation — only the unread case above may surface during one.
    const conversationActive = s.conversationId !== null && !conversationJustEnded;
    if (conversationActive) {
      return null;
    }

    // CASE 5 — just after a conversation closed.
    if (conversationJustEnded) {
      return { message: t('teaserClosed'), ...presenter };
    }

    // CASE 3 — an in-progress order (only when an order provider is wired;
    // see resolvePendingOrderSignal — false by default, never invented).
    if (hasPendingOrder) {
      return { message: t('teaserOrder'), ...presenter };
    }

    // CASE 2 — a returning, logged-in customer whose name we know.
    if (options.customerId && customerDisplayName) {
      return { message: tf('teaserWelcomeBack', { name: customerDisplayName }), ...presenter };
    }

    // CASE 1 — first-time visitor (or a logged-in one whose name hasn't
    // resolved yet — the generic welcome is a safe fit either way).
    return { message: t('teaserNewVisitor'), ...presenter };
  }

  function hideTeaser(): void {
    greetingTeaser?.hide();
  }

  // Explicit dismissal (× or Escape): hides AND suppresses the teaser for
  // the rest of this browsing session (sessionStorage — resets next session).
  function dismissTeaser(): void {
    state.setTeaserDismissed();
    hideTeaser();
  }

  // The single gate every show request passes through. No-ops unless the
  // panel is closed, the customer hasn't dismissed the teaser this session,
  // it isn't already showing, and there's actually a message to show. Also
  // never shows while the tab is hidden — a teaser the customer can't see
  // would just burn its one appearance.
  function showTeaserIfEligible(): void {
    if (!greetingTeaser || !elements) {
      return;
    }

    if (
      state.getState().openState !== 'closed' ||
      state.isTeaserDismissed() ||
      document.hidden
    ) {
      return;
    }

    const content = selectTeaserContent();
    if (!content) {
      return;
    }

    // CASE 4 (a genuinely new unread reply) is allowed to upgrade an
    // already-visible lower-priority teaser in place — the badge already
    // reflects the new message the instant it arrives, so a "welcome back"/
    // "order in progress" bubble already on screen would otherwise sit there
    // unchanged, no longer matching what the badge is telling the customer.
    // Any other case leaves an already-visible teaser alone (render() below
    // is a plain content swap and show() no-ops when already visible, so
    // this never restarts the entry animation or double-shows anything).
    const isUnreadCase = state.getState().unreadCount > 0;
    if (greetingTeaser.isVisible() && !isUnreadCase) {
      return;
    }

    greetingTeaser.render(content);
    greetingTeaser.show();
    // No auto-hide: the teaser stays until the customer removes it (× or
    // Escape) or opens the widget — a calm, persistent "we're here", never
    // a message that vanishes before it's read.
  }

  function scheduleInitialTeaser(): void {
    if (teaserShowTimer !== null) {
      return;
    }
    teaserShowTimer = setTimeout(() => {
      teaserShowTimer = null;
      showTeaserIfEligible();
    }, TEASER_SHOW_DELAY_MS);
  }

  function cancelTeaser(): void {
    if (teaserShowTimer !== null) {
      clearTimeout(teaserShowTimer);
      teaserShowTimer = null;
    }
    greetingTeaser?.hide();
  }

  // Best-effort CASE 3 signal: only when a caller has actually wired an
  // order/customer provider. It's per-conversation by shape, so this only
  // has a conversation to ask about once one exists — meaning CASE 3 is
  // inherently unavailable for a pre-conversation greeting in the default
  // (unwired) build. Kept as the honest hook rather than inventing orders.
  function resolvePendingOrderSignal(): void {
    const provider = options.getCustomerInfo;
    const conversationId = state.getState().conversationId;

    if (!provider || !conversationId) {
      return;
    }

    void provider(conversationId)
      .then((info) => {
        hasPendingOrder = Boolean(info?.latestOrder);
      })
      .catch(() => {
        hasPendingOrder = false;
      });
  }

  // Opening the panel never starts a conversation by itself — only an
  // explicit "Start new chat" click (or "Try again", or resuming one that
  // already exists — see mount()) may call connectToConversation(). The
  // panel's content (loading a real conversation, or a "Start new chat"
  // prompt) is decided once at mount and doesn't need deciding again here.
  function open(): void {
    // A close animation still in flight means the panel is mid-fade-out
    // with .panel.hidden not yet set — cancel it synchronously so reopening
    // doesn't fight the tail end of that animation (see close() below; the
    // callback itself clears pendingClose).
    pendingClose?.();

    // Opening the widget always dismisses any teaser instantly — it has
    // served its purpose. Cancels the pending-show timer too, so a teaser
    // never pops open behind an already-open panel.
    cancelTeaser();

    state.setOpenState('open');
    renderOpenState();
    messageList?.scrollToBottom();
    // Opening onto the latest messages IS reading them.
    markReadIfViewing();
    renderUnreadIndicators();

    // Keyboard/screen-reader users land somewhere they can act on
    // immediately: the composer if it can actually accept typing yet,
    // otherwise the dialog itself (panel.tabIndex = -1, see dom.ts) rather
    // than the close button — opening a dialog shouldn't hand focus
    // straight to "leave".
    if (composerReady) {
      composer?.focus();
    } else {
      elements?.panel.focus();
    }
  }

  function close(): void {
    if (!elements || pendingClose) {
      return;
    }

    // Escape closes the widget regardless of where focus currently is, so
    // only reclaim focus if it was actually inside the panel being hidden —
    // otherwise this would steal focus from an unrelated part of the page.
    const hadFocusInPanel = elements.panel.contains(document.activeElement);
    const panel = elements.panel;

    panel.classList.add('flix-chat-widget__panel--closing');

    const finishClose = (): void => {
      window.clearTimeout(fallbackTimeoutId);
      panel.removeEventListener('animationend', handleAnimationEnd);
      panel.classList.remove('flix-chat-widget__panel--closing');
      pendingClose = null;

      state.setOpenState('closed');
      renderOpenState();
      // renderOpenState just reset the launcher's aria-label — re-apply the
      // unread suffix (and badge state) on top of it.
      renderUnreadIndicators();

      if (hadFocusInPanel) {
        elements?.floatingButton.focus();
      }
    };

    const handleAnimationEnd = (event: AnimationEvent): void => {
      if (event.target === panel) {
        finishClose();
      }
    };

    const fallbackTimeoutId = window.setTimeout(finishClose, CLOSE_ANIMATION_FALLBACK_MS);
    panel.addEventListener('animationend', handleAnimationEnd);

    // Lets open() (or a second close(), via the guard above) unwind this
    // cleanly if it runs before the animation finishes on its own.
    pendingClose = () => {
      window.clearTimeout(fallbackTimeoutId);
      panel.removeEventListener('animationend', handleAnimationEnd);
      panel.classList.remove('flix-chat-widget__panel--closing');
      pendingClose = null;
    };
  }

  function toggle(): void {
    if (state.getState().openState === 'open') {
      close();
    } else {
      open();
    }
  }

  function handleConnectionStateChange(connectionState: ConnectionState): void {
    const status = toWidgetConnectionStatus(connectionState);

    if (status !== state.getState().connectionStatus) {
      state.setConnectionStatus(status);
      renderConnectionStatus();
    }
  }

  // Messages from the customer's previous chat whose support channel was
  // deleted that never reached support: offered here, one press each. A
  // convenience only — if it cannot be loaded, nothing else is in the way.
  async function offerCarryOver(): Promise<void> {
    try {
      const offers = await options.client.listCarryOverOffers();
      if (bodyView === 'live') {
        messageList?.showCarryOver(offers, (offer) => options.client.carryOverMessage(offer.messageId));
      }
    } catch (error) {
      console.warn('Could not load messages from a previous chat.', error);
    }
  }

  async function loadMessageHistory(conversationId: string): Promise<void> {
    try {
      const history = await options.client.listMessages(conversationId);
      messageList?.renderMessages(history);
      void offerCarryOver();
    } catch (error) {
      console.error('Failed to load message history.', error);
      messageList?.showError(t('failedToLoadMessages'), () => {
        void loadMessageHistory(conversationId);
      });
    }
  }

  // The composer never talks to the SDK itself — it only emits text and
  // selected files. Uploading, sending, tracking loading/error state, and
  // deciding success/failure all belong to the widget. The sent message is
  // never appended here: it reaches the list the same way every other
  // message does, through the existing subscription below.
  async function handleComposerSend(text: string, attachmentIds: string[]): Promise<void> {
    const conversationId = state.getState().conversationId;

    if (!conversationId || !composer) {
      return;
    }

    composer.setSending(true);
    composer.setSendError(null);
    messageList?.scrollToBottom();

    const replyToMessageId = replyingToMessage?.id ?? null;

    try {
      await options.client.sendMessage({
        conversationId,
        body: text.length > 0 ? text : undefined,
        attachmentIds,
        replyToMessageId,
      });
      composer.clear();
      composer.clearAttachments();
      clearReply();
      composer.focus();
      composer.setSending(false);
    } catch (error) {
      composer.setSending(false);
      composer.setSendError(t('failedToSend'));
      console.error('Failed to send message.', error);
    }
  }

  // ---- Message interactions: reply, reactions, copy, report ----

  function replyPreviewLabel(message: ChatMessage): string {
    return message.senderType === 'CUSTOMER' ? t('replyingToYou') : t('replyingToSupport');
  }

  function startReply(message: ChatMessage): void {
    replyingToMessage = message;
    const snippet = message.body.trim().length > 0 ? message.body : '📎';
    composer?.setReplyPreview({ label: replyPreviewLabel(message), snippet });
    composer?.focus();
  }

  function clearReply(): void {
    replyingToMessage = null;
    composer?.setReplyPreview(null);
  }

  async function toggleReaction(message: ChatMessage, emoji: string): Promise<void> {
    if (bodyView !== 'live') {
      return;
    }

    const mine = (message.reactions ?? []).some((r) => r.emoji === emoji && r.mine);

    try {
      const reactions = mine
        ? await options.client.removeReaction(message.id, emoji)
        : await options.client.addReaction(message.id, emoji);
      // The HTTP response is authoritative; the live 'message_reaction' echo
      // to other tabs updates them the same way.
      messageList?.updateReactions(message.id, reactions);
    } catch (error) {
      console.error('Failed to toggle reaction.', error);
    }
  }

  async function reportMessage(message: ChatMessage): Promise<void> {
    try {
      await options.client.reportMessage(message.id);
      composer?.setSendError(t('reported'));
    } catch (error) {
      console.error('Failed to report message.', error);
      composer?.setSendError(t('reportFailed'));
    }
  }

  function copyMessageText(message: ChatMessage): void {
    if (!message.body) {
      return;
    }
    void navigator.clipboard?.writeText(message.body).catch((error: unknown) => {
      console.error('Failed to copy message text.', error);
    });
  }

  function openMessageMenu(message: ChatMessage, x: number, y: number): void {
    if (bodyView !== 'live' || !messageMenu) {
      return;
    }

    const activeEmojis = new Set(
      (message.reactions ?? []).filter((reaction) => reaction.mine).map((reaction) => reaction.emoji),
    );

    messageMenu.open({
      x,
      y,
      activeEmojis,
      canCopy: message.body.trim().length > 0,
      handlers: {
        onReact: (emoji) => void toggleReaction(message, emoji),
        onReply: () => startReply(message),
        onCopy: () => copyMessageText(message),
        onReport: () => void reportMessage(message),
      },
    });
  }

  async function uploadAttachment(localId: string, file: File): Promise<void> {
    const conversationId = state.getState().conversationId;

    if (!conversationId || !composer) {
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      composer.setAttachmentError(localId, t('fileTooLarge'));
      return;
    }

    try {
      const attachment = await options.client.uploadAttachment(
        conversationId,
        file,
        file.name,
        (fraction) => composer?.setAttachmentProgress(localId, fraction),
      );
      composer.setAttachmentUploaded(localId, attachment);
    } catch (error) {
      // 400 here only ever means the server's own upload validation
      // rejected the file itself (unsupported type, or too large) — see
      // features/files/validation.ts — as opposed to a network/server
      // error, so this is the one case worth a more specific message than
      // the generic fallback below.
      const message =
        error instanceof ChatHttpError && error.status === 400
          ? t('unsupportedFileType')
          : t('uploadFailed');
      composer.setAttachmentError(localId, message);
      console.error('Failed to upload attachment.', error);
    }
  }

  function handleFilesSelected(selected: PendingFile[]): void {
    for (const { localId, file } of selected) {
      void uploadAttachment(localId, file);
    }
  }

  function handleRetryAttachment(localId: string, file: File): void {
    composer?.setAttachmentUploading(localId);
    void uploadAttachment(localId, file);
  }

  // Files can only be attached to a live, connected conversation — the same
  // condition under which the composer's attach button is enabled. Drops
  // are ignored (overlay never shown) on the home screen, the read-only
  // history viewer, the login-required screen, etc.
  function canAcceptDroppedFiles(): boolean {
    return (
      composerReady && bodyView === 'live' && state.getState().conversationId !== null
    );
  }

  function dragHasFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  function setupDragAndDrop(): void {
    if (!elements) {
      return;
    }

    const panel = elements.panel;
    const overlay = elements.dropOverlay;

    const showOverlay = (): void => {
      overlay.hidden = false;
    };
    const hideOverlay = (): void => {
      overlay.hidden = true;
    };

    const handleDragOver = (event: DragEvent): void => {
      if (!dragHasFiles(event)) {
        return;
      }
      // preventDefault on a file dragover is what allows a drop to land
      // here at all (and stops the browser from navigating to the file).
      event.preventDefault();

      if (canAcceptDroppedFiles()) {
        showOverlay();
      }
    };

    const handleDragLeave = (event: DragEvent): void => {
      // dragleave fires for every child boundary crossed — only hide when
      // the pointer has actually left the panel entirely.
      if (!panel.contains(event.relatedTarget as Node | null)) {
        hideOverlay();
      }
    };

    const handleDrop = (event: DragEvent): void => {
      if (!dragHasFiles(event)) {
        return;
      }
      event.preventDefault();
      hideOverlay();

      if (!canAcceptDroppedFiles()) {
        return;
      }

      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length > 0) {
        composer?.addFiles(files);
      }
    };

    panel.addEventListener('dragover', handleDragOver);
    panel.addEventListener('dragleave', handleDragLeave);
    panel.addEventListener('drop', handleDrop);

    detachDragAndDrop = () => {
      panel.removeEventListener('dragover', handleDragOver);
      panel.removeEventListener('dragleave', handleDragLeave);
      panel.removeEventListener('drop', handleDrop);
    };
  }

  // Shared by every path that discovers a conversation is no longer active
  // — the customer's own "End chat", a live "conversation_closed" push
  // while the widget is already open, and connectToConversation() finding
  // a mismatch after a reload. All three must leave the widget in the same
  // state: no live socket assumed valid, composer disabled, and an explicit
  // "Start new chat" required rather than silently reconnecting.
  // The home screen: the headline + Start new chat prompt, plus up to
  // three recent conversations underneath (fetched fresh each time, so a
  // just-ended conversation appears immediately). The recents block is
  // appended into the ended-state element once the fetch resolves — and
  // only if the home screen is still what's showing by then.
  function showHome(): void {
    if (!messageList) {
      return;
    }

    messageList.showEnded(homeMessage ?? t('homePrompt'), () => {
      void connectToConversation();
    });

    void loadRecentConversations();
  }

  async function loadRecentConversations(): Promise<void> {
    let section: HTMLElement | null;

    try {
      const conversations = await options.client.listConversations();
      section = createRecentConversationsSection(
        conversations,
        (conversationId) => state.isConversationUnread(conversationId),
        (conversationId) => {
          void showConversationHistoryViewer(conversationId);
        },
      );
    } catch (error) {
      // Home stays fully usable without the recents block — a customer who
      // can't load their history can still start a new chat, so this is
      // logged, never surfaced as a blocking error.
      console.error('Failed to load recent conversations.', error);
      return;
    }

    if (!section || !messageList || bodyView !== 'live') {
      return;
    }

    // Still on the home screen? (A conversation may have started, or a
    // second showHome() may have re-rendered, while the fetch was in
    // flight.) The ended-state element is the marker.
    const endedElement = messageList.element.querySelector('.flix-chat-widget__messages-ended');

    if (!endedElement || state.getState().conversationId !== null) {
      return;
    }

    endedElement.querySelector('.flix-chat-widget__recent')?.remove();
    endedElement.append(section);
  }

  // Only touches messageList's visible content while it's actually the
  // live view — if the customer is currently viewing a read-only past
  // conversation (which reuses messageList, see
  // showConversationHistoryViewer), clobbering it here would corrupt what
  // they're looking at. showLiveConversation() already re-derives the
  // correct home screen from state when they navigate back.
  function showConversationEnded(): void {
    state.setConnectionStatus('idle');
    state.setConversationId(null);
    state.setAssignedStaffName(null);
    state.setAssignedStaffAvatarUrl(null);
    composerReady = false;
    composer?.setReady(false);
    clearReply();
    messageMenu?.close();
    clearStaffTyping();
    renderConnectionStatus();
    renderEndChatButton();

    // Any unread count belonged to the conversation that just ended — there
    // is no conversation left to read it in, so a leftover badge would sit
    // there indefinitely (until some unrelated later conversation happens to
    // be viewed) even though the widget looks fully idle. Reuses
    // markMessagesRead's exact clear-badge/title/pending-notification path.
    markMessagesRead();

    homeMessage = t('conversationEnded');
    // Enables the teaser's "Conversation closed" case (CASE 5) for the
    // rest of this session until a new conversation connects.
    conversationJustEnded = true;

    if (bodyView === 'live') {
      showHome();
    }
  }

  function handleIncomingPayload(payload: unknown): void {
    if (isChatMessage(payload)) {
      // Whether the customer is looking at the latest messages, decided
      // BEFORE appending — appendMessage auto-scrolls when they were at
      // the bottom, which would make the check trivially true afterwards.
      const viewing = isViewingLatestMessages();

      // Same reasoning as showConversationEnded above: while History or the
      // read-only viewer is showing, messageList is either detached or
      // displaying a different (past) conversation's messages entirely —
      // appending a live message into it here would be visibly wrong. The
      // message is already persisted server-side, so a plain reload on
      // return to Live (see showLiveConversation) picks it up regardless.
      if (bodyView === 'live') {
        messageList?.appendMessage(payload);
      }

      // Only support-team messages ever count as unread or make a sound —
      // never the customer's own (their sends echo back through this same
      // subscription) and never system payloads. A staff message landing
      // while the customer is watching the latest messages is read the
      // instant it renders — and seeing the newest message also clears any
      // stale count from earlier.
      if (payload.senderType === 'STAFF') {
        if (viewing) {
          markMessagesRead();
        } else {
          registerUnreadStaffMessage(payload);
        }
      }
      return;
    }

    if (isConversationClosedEvent(payload)) {
      // Ignore closes for a conversation this connection isn't even on
      // anymore (e.g. a stale event arriving just as a new one starts) —
      // the state comparison in connectToConversation already covers that
      // case going forward.
      if (payload.conversationId !== state.getState().conversationId) {
        return;
      }

      options.client.disconnect();
      showConversationEnded();
      return;
    }

    if (isMessageReactionEvent(payload)) {
      // Live reaction update (another tab, or a support member on Discord).
      if (bodyView === 'live') {
        messageList?.updateReactions(payload.messageId, payload.reactions);
      }
      return;
    }

    if (isMessageDeliveryEvent(payload)) {
      // The customer's own message reached support (✓) or did not.
      if (bodyView === 'live') {
        messageList?.updateDelivery(payload.messageId, payload.delivery);
      }
      return;
    }

    if (isStaffTypingEvent(payload)) {
      if (bodyView === 'live') {
        showStaffTyping(payload.staffName);
      }
      return;
    }

    if (isTicketClaimedEvent(payload)) {
      if (payload.conversationId !== state.getState().conversationId) {
        return;
      }

      state.setAssignedStaffName(payload.staffName);
      // Captured for the greeting teaser (and any future presence UI) so it
      // can show the real person — session-only, same as the name.
      state.setAssignedStaffAvatarUrl(payload.staffAvatarUrl ?? null);
      renderConnectionStatus();

      // Live-only, same guard as the ChatMessage branch above — while
      // History/the read-only viewer is showing, messageList is either
      // detached or displaying a different conversation's messages
      // entirely.
      if (bodyView === 'live') {
        // ?? null: the type guard accepts a payload where this field is
        // simply absent (see isTicketClaimedEvent), which the declared
        // TicketClaimedEvent type doesn't itself express.
        messageList?.appendSystemNotice(payload.staffName, payload.staffAvatarUrl ?? null);
      }
    }
  }

  // Closes the ticket server-side (which also archives the Discord channel
  // and revokes this session's token — see chatService.closeConversation),
  // then resets local state so the widget looks and behaves like a
  // brand-new session: no conversation, no live socket, composer disabled
  // until the customer explicitly starts a new chat.
  async function handleEndChat(): Promise<void> {
    const conversationId = state.getState().conversationId;

    if (!conversationId || !elements) {
      return;
    }

    elements.endChatButton.disabled = true;

    try {
      await options.client.closeConversation(conversationId);
    } catch (error) {
      console.error('Failed to end the chat.', error);
      elements.endChatButton.disabled = false;
      return;
    }

    options.client.disconnect();
    showConversationEnded();
    elements.endChatButton.disabled = false;
  }

  function reconnect(): void {
    // Unreachable in practice — the reconnect control only renders once a
    // conversation already exists, which itself requires a customerId — but
    // never assume that at the type level.
    if (!options.customerId) {
      return;
    }

    const conversationId = state.getState().conversationId;

    if (!conversationId) {
      void connectToConversation();
      return;
    }

    options.client.disconnect();
    options.client.connect(options.customerId, conversationId);
  }

  // Shown instead of the normal composer/messageList exactly once per
  // customer — only ever reached from connectToConversation's 428 branch
  // below (Shopify had no name on file and none was stored yet). No back
  // control, like loginRequired: the only way out is submitting a name.
  function showNameRequired(): void {
    if (!elements) {
      return;
    }

    if (!nameRequiredPanel) {
      nameRequiredPanel = createNameRequiredPanel((name) => void handleNameSubmit(name));
    }

    elements.body.replaceChildren(nameRequiredPanel.element);
    elements.footer.hidden = true;
    nameRequiredPanel.focus();
  }

  async function handleNameSubmit(name: string): Promise<void> {
    if (!nameRequiredPanel) {
      return;
    }

    nameRequiredPanel.setSubmitting(true);
    nameRequiredPanel.setError(null);

    try {
      await options.client.setCustomerName(name);
    } catch (error) {
      nameRequiredPanel.setSubmitting(false);
      nameRequiredPanel.setError(t('nameSaveFailed'));
      console.error('Failed to save customer name.', error);
      return;
    }

    nameRequiredPanel.setSubmitting(false);
    // A name now exists, so this retry's bootstrap call succeeds past the
    // 428 branch below — same connectToConversation() used everywhere else,
    // no separate "resume after name" path needed.
    void connectToConversation();
  }

  // createConversation is always safe to call, with no pre-existing token
  // required — it's the SDK's own bootstrap/authentication step. Per Wave 2
  // it transparently returns the customer's existing active conversation
  // rather than creating a duplicate, and it's also what mints the session
  // token every other call now requires, so this always runs on connect
  // rather than trying to resume a persisted conversationId without one.
  async function connectToConversation(): Promise<void> {
    const customerId = options.customerId;

    // Should be unreachable — the login-required screen never wires up a
    // path that calls this — but a conversation must never be bootstrapped
    // without a real, logged-in customer id.
    if (!customerId) {
      return;
    }

    // Restores elements.body to messageList first — a no-op on every normal
    // call site (body already shows it), but necessary on the retry after
    // showNameRequired() swapped it out below. Then shows the same skeleton
    // already used when initialize() resumes an existing conversation —
    // applying it here too means "Start new chat" stops leaving the old
    // home screen visible for the whole bootstrap + history-fetch window.
    // Presentation only: doesn't change what runs below or in what order.
    if (elements && messageList) {
      bodyView = 'live';
      elements.body.replaceChildren(messageList.element);
      elements.footer.hidden = false;
    }
    messageList?.showLoading();

    // Captured before the call below overwrites it — this is whatever
    // conversationId was already on record (persisted from a previous page,
    // or null on a genuinely first-ever bootstrap).
    const previousConversationId = state.getState().conversationId;
    state.setConnectionStatus('connecting');
    renderConnectionStatus();

    try {
      const conversation = await options.client.createConversation(customerId);

      if (previousConversationId !== null && previousConversationId !== conversation.id) {
        // The conversation this customer last had is no longer the active
        // one — closed elsewhere since their last visit (staff closing it
        // from Discord, most commonly). createConversation() already opened
        // a fresh conversation in its place, but silently connecting to it
        // would look exactly like the old one never ended. Surface that
        // instead, exactly like the customer-initiated end-chat path — only
        // actually connect once they choose to continue, at which point
        // this same createConversation() call returns this same still-open
        // conversation rather than opening a second new one.
        showConversationEnded();
        return;
      }

      state.setConversationId(conversation.id);
      state.setAssignedStaffName(conversation.assignedStaffName);
      renderEndChatButton();
      composerReady = true;
      composer?.setReady(true);
      // A live conversation exists again — the "just closed" teaser state
      // no longer applies.
      conversationJustEnded = false;
      resolvePendingOrderSignal();
      options.client.connect(customerId, conversation.id);

      // Per spec: request browser-notification permission once the
      // customer actually starts/resumes a conversation — never on plain
      // page load, and this is the one place every such path (an explicit
      // "Start new chat", the panel auto-resuming an existing one) funnels
      // through.
      maybeRequestNotificationPermission();

      await loadMessageHistory(conversation.id);
      // History just rendered scrolled to the newest message — if the
      // panel is open and visible, that's a read. (On the closed-panel
      // background resume this is a no-op and any persisted count stays.)
      markReadIfViewing();
    } catch (error) {
      // Shopify had no name on file and none was stored yet (see
      // features/customers) — show the one-time name screen instead of the
      // generic error. Not a failure to communicate as one: the customer
      // hasn't done anything wrong, the bootstrap just needs one more piece
      // of information first.
      if (error instanceof ChatHttpError && error.status === NAME_REQUIRED_STATUS) {
        state.setConnectionStatus('idle');
        renderConnectionStatus();
        showNameRequired();
        return;
      }

      // Distinct from a post-success WebSocket drop (see toWidgetConnectionStatus
      // above): no conversation exists yet, so there's no reconnect banner and no
      // "disconnected" header line to show — only the message list's own error
      // + Try again, which retries this same bootstrap from scratch.
      state.setConnectionStatus('creationFailed');
      renderConnectionStatus();
      console.error('Failed to start the support chat connection.', error);
      messageList?.showError(t('unableToStart'), () => {
        void connectToConversation();
      });
    }
  }

  // Returns to the live conversation view from the read-only viewer or the
  // customer-info panel. Re-derives the correct content from current state
  // rather than assuming messageList's existing children are still valid —
  // they aren't, right after leaving the read-only viewer (see
  // showConversationHistoryViewer), which reuses the same messageList
  // instance to render a different, past conversation's messages.
  function showLiveConversation(): void {
    if (!elements || !messageList) {
      return;
    }

    bodyView = 'live';
    elements.body.replaceChildren(messageList.element);
    elements.footer.hidden = false;

    const conversationId = state.getState().conversationId;

    if (conversationId) {
      messageList.showLoading();
      void loadMessageHistory(conversationId);
    } else {
      showHome();
    }
  }

  // Read-only view of one past conversation, entered from the home
  // screen's recent-conversations rows: reuses the same messageList
  // instance and its existing renderMessages(), just fed a different,
  // read-only-sourced message array — see conversationHistory.ts's
  // createHistoryViewer for the wrapping banner + back control. No composer
  // (footer stays hidden) — reopening a possibly Discord-channel-deleted
  // closed conversation isn't safe; "Start new chat" is the way to
  // follow up.
  async function showConversationHistoryViewer(conversationId: string): Promise<void> {
    if (!elements || !messageList) {
      return;
    }

    // The customer chose to open this one — whatever was unread in it is
    // read now, regardless of whether the fetch below succeeds.
    state.markConversationRead(conversationId);

    bodyView = 'historyViewer';
    elements.footer.hidden = true;

    const wrapper = createHistoryViewer(showLiveConversation, messageList.element);
    elements.body.replaceChildren(wrapper);
    messageList.showLoading();

    try {
      const messages = await options.client.getConversationHistory(conversationId);
      messageList.renderMessages(messages);
    } catch (error) {
      console.error('Failed to load conversation history.', error);
      messageList.showError(t('failedToLoadConversation'), () => {
        void showConversationHistoryViewer(conversationId);
      });
    }
  }

  // Swaps the panel body over to the customer-info panel and asks the
  // caller-supplied provider for data. Only reachable at all when
  // options.getCustomerInfo was actually passed in — see initialize().
  function showCustomerInfo(): void {
    const conversationId = state.getState().conversationId;

    if (!elements || !options.getCustomerInfo || !messageList || !conversationId) {
      return;
    }

    if (!customerInfoPanel) {
      customerInfoPanel = createCustomerInfoPanel(() => showLiveConversation());
    }

    elements.body.replaceChildren(customerInfoPanel.element);
    elements.footer.hidden = true;
    customerInfoPanel.showLoading();

    options
      .getCustomerInfo(conversationId)
      .then((info) => {
        if (!customerInfoPanel) {
          return;
        }

        if (info) {
          customerInfoPanel.render(info);
        } else {
          customerInfoPanel.showUnavailable();
        }
      })
      .catch((error: unknown) => {
        console.error('Failed to load customer info.', error);
        customerInfoPanel?.showUnavailable();
      });
  }

  function initialize(): void {
    if (elements) {
      return;
    }

    elements = createWidgetDom();
    injectWidgetStyles();
    applyWidgetTheme(elements, options.theme);
    document.body.append(elements.container);

    eventBindings = bindWidgetEvents(elements, {
      onToggle: toggle,
      onClose: close,
      onReconnect: reconnect,
      onEndChat: () => void handleEndChat(),
    });

    renderOpenState();

    // Greeting teaser — set up for every visitor (logged in or not: a
    // logged-out "new visitor" still gets CASE 1, and clicking simply opens
    // the widget onto the login prompt, a normal conversion funnel). Built
    // before the login-required early return below so it's never skipped.
    // Its own eligibility gate decides the final show at fire time.
    setupGreetingTeaser();

    // No logged-in customer: the launcher still renders and opens/closes
    // normally, but the panel shows a login/register prompt instead of a
    // conversation. No conversation is created, no message list or composer
    // exists, and nothing connects — creating conversations stays limited to
    // logged-in customers exactly as before (see CreateWidgetOptions above).
    if (!options.customerId) {
      elements.footer.hidden = true;
      loginRequired = createLoginRequiredScreen({
        loginUrl: options.loginUrl,
        registerUrl: options.registerUrl,
        ...options.loginScreen,
      });
      elements.body.append(loginRequired.element);
      // renderOpenState() above ran before this screen existed; a panel left
      // open from the previous page is showing it already.
      if (state.getState().openState === 'open') {
        loginRequired.activate();
      }
      return;
    }

    messageList = createMessageListRenderer(options.client, {
      onOpenMenu: openMessageMenu,
      onToggleReaction: (message, emoji) => void toggleReaction(message, emoji),
      onResend: (message) => options.client.resendMessage(message.id),
    });
    elements.body.append(messageList.element);

    // The floating message-actions menu (reactions + Reply/Copy/Report),
    // one instance reused for every message, appended to the widget root.
    messageMenu = createMessageMenu();
    elements.container.append(messageMenu.element);

    // Scrolling down to the newest messages is how "scrolled away" unread
    // state gets read — fires on the customer's own scrolling AND on every
    // programmatic scrollToBottom (renderMessages/appendMessage), so the
    // usual open/append flows clear through here for free.
    messageList.element.addEventListener('scroll', markReadIfViewing);

    // Returning to the tab while the panel sits open at the latest
    // messages is also a read; the listener lives on document, so unlike
    // the scroll listener above it must be detached in destroy().
    const handleVisibilityChange = (): void => markReadIfViewing();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    detachVisibilityListener = () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);

    // One-time gesture-unlock listeners for WebAudio (see notificationSound
    // — without a prior gesture, browsers keep the context silent).
    prepareNotificationSound();

    // The badge/title may carry a count persisted from a previous page —
    // render it immediately, before any network work settles.
    renderUnreadIndicators();

    // Non-blocking: never delays first paint or the home screen's own
    // "Start new chat" affordance. Read-only (see getCustomerProfile — no
    // live Shopify call), so a first-time visitor with nothing stored yet
    // just keeps the default "Flix Support" title until their first
    // conversation resolves one (see connectToConversation's 428 handling).
    options.client
      .getCustomerProfile()
      .then((profile) => {
        if (elements && profile.displayName) {
          // Kept for the greeting teaser's "Welcome back, {name}" case too.
          customerDisplayName = profile.displayName;
          elements.headerTitle.textContent = tf('greeting', { name: profile.displayName });
        }
      })
      .catch((error: unknown) => {
        console.error('Failed to load customer profile.', error);
      });

    composer = createComposer({
      onSend: (text, attachmentIds) => void handleComposerSend(text, attachmentIds),
      onFilesSelected: handleFilesSelected,
      onRetryAttachment: handleRetryAttachment,
      onCancelReply: clearReply,
      onTyping: () => {
        const conversationId = state.getState().conversationId;
        if (conversationId) {
          options.client.sendTyping(conversationId);
        }
      },
    });
    elements.footer.append(composer.element);

    // stopPropagation: the menu lives inside the header, whose own click
    // opens the customer-info panel — flipping a preference must never
    // also navigate. The menu deliberately stays open so the icon/state
    // change is visible right where the click landed.
    elements.soundToggleButton.addEventListener('click', (event) => {
      event.stopPropagation();
      state.setSoundEnabled(!state.getState().soundEnabled);
      renderSoundToggle();
    });
    renderSoundToggle();

    // A blocked (denied) toggle is inert — see renderNotificationsToggle,
    // which also disables the button in that state.
    elements.notificationsToggleButton.addEventListener('click', (event) => {
      event.stopPropagation();

      if (getNotificationPermission() === 'denied') {
        return;
      }

      const next = !state.getState().browserNotificationsEnabled;
      state.setBrowserNotificationsEnabled(next);
      renderNotificationsToggle();

      // Turning it on is itself a real, deliberate interaction with the
      // widget — a second valid trigger for the permission prompt (spec:
      // "أو بعد أول تفاعل حقيقي مع الويدجت") for a customer who opens
      // settings before ever starting a conversation.
      if (next) {
        maybeRequestNotificationPermission();
      }
    });
    renderNotificationsToggle();

    setupDragAndDrop();

    if (options.getCustomerInfo) {
      elements.header.classList.add('flix-chat-widget__header--clickable');
      elements.header.setAttribute('role', 'button');
      elements.header.setAttribute('tabindex', '0');
      elements.header.addEventListener('click', showCustomerInfo);
      elements.header.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          showCustomerInfo();
        }
      });
    }

    renderConnectionStatus();
    renderEndChatButton();

    unsubscribeConnectionState = options.client.onConnectionStateChange(
      handleConnectionStateChange,
    );
    unsubscribeMessages = options.client.subscribe(handleIncomingPayload);

    // Bootstrapping (createConversation — a real ticket and Discord
    // channel) only ever happens two ways: automatically here, but only to
    // resume a conversation that already exists (never to start one), or
    // explicitly via the "Start new chat" / "Try again" click inside
    // showEnded/showError. Opening the panel (see open() above) never
    // triggers it by itself, and neither does mounting/reloading with no
    // conversation on record — whether that's a genuinely first-ever visit
    // or a previous one that ended, the customer must always click
    // something first.
    if (state.getState().conversationId !== null) {
      messageList.showLoading();
      void connectToConversation();
    } else {
      showHome();
    }
  }

  // Builds the teaser, appends it to the widget container (a sibling of the
  // launcher/panel), wires Escape-to-dismiss, and schedules the one-shot
  // appearance. Called once from initialize() for every visitor.
  function setupGreetingTeaser(): void {
    if (!elements) {
      return;
    }

    greetingTeaser = createGreetingTeaser({
      onOpen: open,
      onDismiss: dismissTeaser,
    });
    elements.container.append(greetingTeaser.element);

    // Escape dismisses a visible teaser (and only then) — capture phase +
    // stopPropagation so it doesn't also reach the panel's own Escape
    // handling, which is meant for the open dialog.
    const handleTeaserKeydown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && greetingTeaser?.isVisible()) {
        event.stopPropagation();
        dismissTeaser();
      }
    };
    document.addEventListener('keydown', handleTeaserKeydown, true);
    detachTeaserKeydown = () =>
      document.removeEventListener('keydown', handleTeaserKeydown, true);

    scheduleInitialTeaser();
  }

  function destroy(): void {
    unsubscribeConnectionState?.();
    unsubscribeConnectionState = null;

    unsubscribeMessages?.();
    unsubscribeMessages = null;

    detachVisibilityListener?.();
    detachVisibilityListener = null;

    clearPendingBrowserNotification();
    activeBrowserNotification?.close();
    activeBrowserNotification = null;

    if (teaserShowTimer !== null) {
      clearTimeout(teaserShowTimer);
      teaserShowTimer = null;
    }
    if (staffTypingTimer !== null) {
      clearTimeout(staffTypingTimer);
      staffTypingTimer = null;
    }
    detachTeaserKeydown?.();
    detachTeaserKeydown = null;
    greetingTeaser?.destroy();
    greetingTeaser = null;

    detachDragAndDrop?.();
    detachDragAndDrop = null;

    // Leave the host page's title exactly as found — strip our unread
    // prefix if it's currently applied.
    updatePageTitle(0);

    eventBindings?.detach();
    eventBindings = null;

    composer?.destroy();
    composer = null;

    messageMenu?.destroy();
    messageMenu = null;

    messageList = null;
    customerInfoPanel = null;
    nameRequiredPanel = null;
    bodyView = 'live';
    homeMessage = null;
    composerReady = false;
    replyingToMessage = null;
    pendingClose = null;

    elements?.container.remove();
    elements = null;

    options.client.disconnect();
  }

  return { initialize, destroy, open, close, toggle };
}
